/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import TreeNode from "App/utils/globe/chunk_lod/tree/node";
import DataHandler from "App/utils/globe/chunk_lod/data/data_handler";
import PointsMapper from "App/utils/globe/chunk_lod/data/points_mapper";
import {vec3} from "gl-matrix";
import DatLoader from "App/utils/globe/chunk_lod/data/dat_loader";
import IAddress from "App/utils/globe/chunk_lod/adress";

export default
class ProgressiveDataHandler extends DataHandler {
    private _pointsMapper: PointsMapper;
    private _positionsArray: ArrayBuffer;
    private _normalsArray: ArrayBuffer;
    private _texcoordsArray: ArrayBuffer;
    private _layersArray: ArrayBuffer;
    private _surfaceStylesArray: ArrayBuffer;
    private _metaData: { col: number; nofTextureLODS: number; nc: number; level: number; heights: any[]; bbox: { min: vec3; max: vec3 }; widths: any[]; nofFaces: number; row: number; nofPoints: number, baseAddress: string };

    constructor(aNode: TreeNode) {
        super(aNode);

        this._pointsMapper = null;
        this._metaData = null;
    }

    /**
     * Load the points mapping table, geometry and texture from the input data
     * @private
     */
    public fill(aData: any, aAddress: IAddress) {
        let patchTranslationOffset = aAddress.getTranslationOffset();
        switch (aData.type) {
            case "geo+jpg":
                let datLoader = new DatLoader(aAddress);
                if (datLoader.load(aData.data)) {
                    this._pointsMapper = new PointsMapper(this._node.getAddress());
                    this._pointsMapper.fill(datLoader);
                    this._positionsArray = datLoader.getPositions();
                    this._normalsArray = datLoader.getNormals();
                    this._texcoordsArray = datLoader.getTexcoords();
                    this._layersArray = datLoader.getLayers();
                    this._surfaceStylesArray = datLoader.getSurfaceStyles();
                    this._indicesArray = datLoader.getIndices();
                    this._textureData = datLoader.getJpgBuffer();
                    this._metaData = datLoader.getMetadata();

                    // apply translation offset to bbox because the patches geometry is at local zero so we need to translate them in shader
                    this._metaData.bbox.min[0] += patchTranslationOffset[0];
                    this._metaData.bbox.min[2] += patchTranslationOffset[1];
                    this._metaData.bbox.max[0] += patchTranslationOffset[0];
                    this._metaData.bbox.max[2] += patchTranslationOffset[1];

                    this._metaData.baseAddress = aAddress.getDataBaseUrl();
                } else {
                    console.log("Error: failed to parse .dat file");
                }
                break;
            default:
                console.log("Error: unsupported data file");
        }
    }

    public getAttributes(aAddress: IAddress): any {
        let attributes: any = {};

        // based on points mapper we found the offset and size of the points which belongs to the aAddress in current tile and current level
        let positions = this._pointsMapper.getNodePointsPositionsOffsetAndSize(aAddress);
        attributes.positions = this._positionsArray.slice(positions.offset, positions.offset + positions.size);

        let normals = this._pointsMapper.getNodePointsNormalsOffsetAndSize(aAddress);
        attributes.normals = this._normalsArray.slice(normals.offset, normals.offset + normals.size);

        let texcoords = this._pointsMapper.getNodePointsTexCoordsOffsetAndSize(aAddress);
        attributes.texcoords = this._texcoordsArray.slice(texcoords.offset, texcoords.offset + texcoords.size);

        let layers = this._pointsMapper.getNodePointsLayersOffsetAndSize(aAddress);
        attributes.layers = this._layersArray.slice(layers.offset, layers.offset + layers.size);

        let surfaceStyles = this._pointsMapper.getNodePointsSurfaceStyleOffsetAndSize(aAddress);
        attributes.surfacestyles = this._surfaceStylesArray.slice(surfaceStyles.offset, surfaceStyles.offset + surfaceStyles.size);

        return attributes;
    }

    public getIncrementPointsCount(aAddress: IAddress): number {
        return this._pointsMapper.getNodePointsPositionsOffsetAndSize(aAddress).size / (4 * 3);
    }

    public getMetadata(): { col: number; nofTextureLODS: number; nc: number; level: number; heights: any[]; bbox: { min: vec3; max: vec3 }; widths: any[]; nofFaces: number; row: number; nofPoints: number, baseAddress: string } {
        return this._metaData;
    }

    public getHasNode(aAddress: IAddress): boolean {
        return this._pointsMapper.getNodePointsLayersOffsetAndSize(aAddress).size !== 0;
    }
}
